import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { safeParseJson } from '@appesteem/aeshared-js/loadwatch';

// Helper to get the latest event with rectangles for a given time
export function getActiveRectEvent(events, currentTime) {
  // Only consider events with at least one rectangle property
  // and only consider WinHookEvents (not MouseActionEvents, as their windows rectangles are based on mouse position)
  const candidates = events.filter(ev =>
      (ev.WindowBoundingRectangle && !ev.WindowBoundingRectangle.IsEmpty) ||
      (ev.ElementBoundingRectangle && !ev.ElementBoundingRectangle.IsEmpty)
  );
  // Find the latest event before or at currentTime
  let active = null;
  for (const ev of candidates) {
    if (typeof ev.VideoOffset === 'number' && ev.VideoOffset / 1000.0 <= currentTime) {
      if (!active || ev.VideoOffset > active.VideoOffset) {
        active = ev;
      }
    }
  }
  return active;
}

// Helper to get the latest event with rectangles for a given time
export function getActiveForegroundEvent(events, currentTime) {
  // Only consider events with at least one rectangle property
  // and only consider WinHookEvents (not MouseActionEvents, as their windows rectangles are based on mouse position)
  const candidates = events.filter(ev =>
    ev.EventType === "WinHookEvent" && 
    ev.Action === "EVENT_SYSTEM_FOREGROUND" &&
    (
      (ev.WindowBoundingRectangle && !ev.WindowBoundingRectangle.IsEmpty) ||
      (ev.ElementBoundingRectangle && !ev.ElementBoundingRectangle.IsEmpty)
    )
  );
  // Find the latest event before or at currentTime
  let active = null;
  for (const ev of candidates) {
    if (typeof ev.VideoOffset === 'number' && ev.VideoOffset / 1000.0 <= currentTime) {
      if (!active || ev.VideoOffset > active.VideoOffset) {
        active = ev;
      }
    }
  }
  return active;
}

// we have two versions of WindowBoundingRectangle/ElementBoundingRectangle
export function cleanRect(rect) {
  if (!rect || rect.IsEmpty) return rect;
  if (typeof rect === "string") {
    let arr = rect.split(",").map(num => Number(num.trim()));
    return {
      X: arr[0],
      Y: arr[1],
      Width: arr[2] - arr[0] > 0 ? arr[2] - arr[0] : 0,
      Height: arr[3] - arr[1] > 0 ? arr[3] - arr[1] : 0,
    }
  } else if (typeof rect === "object") {
    return {
      X: rect.X,
      Y: rect.Y,
      Width: rect.Width,
      Height: rect.Height,
    }
  }
}


// Rectangle overlay component
function RectangleOverlay({
  events = [],
  currentTime = 0,
  videoRef,
  displayDuration = 5,
  viewConfig = {},
}) {
  const [activeEvent, setActiveEvent] = useState(null);
  const timeoutRef = useRef();

  useEffect(() => {
    // Find the latest event with rectangles for the current time
    const event = getActiveRectEvent(events, currentTime);
    setActiveEvent(event);
    // Clear any previous timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (event) {
      // Hide after displayDuration seconds or when a new event is found
      timeoutRef.current = setTimeout(() => {
        setActiveEvent(null);
      }, displayDuration * 1000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentTime, events, displayDuration]);

  // Get video size and position for scaling
  const [videoRect, setVideoRect] = useState({ width: 1, height: 1, left: 0, top: 0 });
  useEffect(() => {
    if (videoRef && videoRef.current) {
      const rect = videoRef.current.getBoundingClientRect();
      setVideoRect(rect);
    }
  }, [videoRef, currentTime]);

  if (!activeEvent) return null;

  // Helper to convert rectangle to style
  function rectToStyle(rect, color) {
    if (!rect || rect.IsEmpty) return { display: 'none' };
    // Scale to video size
    const scaleX = videoRect.width / videoRef.current.videoWidth;
    const scaleY = videoRect.height / videoRef.current.videoHeight;
    // Choose glow color based on border color
    let boxShadow;
    if (color === '#ab47bc') {
      boxShadow = '0 0 12px 3px rgba(186, 104, 200, 0.38)'; // lighter purple glow
      color = '#ba68c8'; // lighter/brighter purple
    } else if (color === '#00bcd4') {
      boxShadow = '0 0 12px 3px rgba(0, 188, 212, 0.32)'; // cyan glow
    } else if (color === '#ebe00de2') {
      boxShadow = '0 0 14px 4px rgba(235, 224, 13, 0.38)'; // light yellow glow
      color = '#fff176'; // lighter yellow
    } else if (color === '#22ff00ff') {
      boxShadow = '0 0 14px 4px rgba(34, 255, 0, 0.32)'; // green glow
      color = '#69f0ae'; // lighter green
    } else {
      boxShadow = '0 0 10px 2px rgba(0,0,0,0.12)';
    }
    return {
      position: 'absolute',
      left: rect.X * scaleX,
      top: rect.Y * scaleY,
      width: rect.Width * scaleX,
      height: rect.Height * scaleY,
      border: `2px solid ${color}`,
      boxSizing: 'border-box',
      pointerEvents: 'none',
      zIndex: 10,
      boxShadow,
      transition: 'left 0.08s, top 0.08s',
    };
  }

  // Helper to convert mouse position to style
  function mouseToStyle(mousePos) {
    if (!mousePos) return { display: 'none' };
    const scaleX = videoRect.width / videoRef.current.videoWidth;
    const scaleY = videoRect.height / videoRef.current.videoHeight;
    const size = 22; // px, diameter of the circle
    return {
      position: 'absolute',
      left: mousePos.X * scaleX - size / 2,
      top: mousePos.Y * scaleY - size / 2,
      width: size,
      height: size,
      border: '2.5px solid #ffb300', // orange/yellow border
      borderRadius: '50%',
      background: 'rgba(255, 193, 7, 0.22)', // yellow highlighter style
      boxShadow: '0 0 8px 2px rgba(255, 193, 7, 0.25)', // subtle glow
      pointerEvents: 'none',
      zIndex: 20,
      boxSizing: 'border-box',
      transition: 'left 0.08s, top 0.08s',
    };
  }

  // Parse mouse position from Extras
  let mousePos = null;
  if (activeEvent && activeEvent.Extras) {
    try {
      const extras = typeof activeEvent.Extras === 'string'
        ? safeParseJson(activeEvent.Extras)
        : activeEvent.Extras;
      if (extras && extras.mousePosition && typeof extras.mousePosition.X === 'number' && typeof extras.mousePosition.Y === 'number') {
        mousePos = extras.mousePosition;
      }
    } catch (e) {
      // Defensive: ignore unexpected errors reading Extras shape.
    }
  }

  return (
    <>
      {/* Active window border (purple) – foreground WinHookEvent */}
      {!!viewConfig.overlayActiveWindow && activeEvent.WindowBoundingRectangle && activeEvent.EventType === "WinHookEvent" && activeEvent.Action === "EVENT_SYSTEM_FOREGROUND" && !activeEvent.WindowBoundingRectangle.IsEmpty && (
        <div style={rectToStyle(cleanRect(activeEvent.WindowBoundingRectangle), '#ab47bc')} /> // purple
      )}
      {/* Foreground window rect on mouse click (yellow) */}
      {!!viewConfig.overlayClickWindow && activeEvent.Extras && activeEvent.EventType === "MouseActionEvent" && (activeEvent.Action === "WM_LBUTTONDOWN" || activeEvent.Action === "WM_RBUTTONDOWN") && !activeEvent.Extras.foregroundWindowRect?.IsEmpty && (
        <div style={rectToStyle(cleanRect(activeEvent.Extras.foregroundWindowRect), '#ebe00de2')} /> // light yellow
      )}
      {/* Click window bounding rectangle (green) */}
      {!!viewConfig.overlayClickWindow && activeEvent.WindowBoundingRectangle && activeEvent.EventType === "MouseActionEvent" && (activeEvent.Action === "WM_LBUTTONDOWN" || activeEvent.Action === "WM_RBUTTONDOWN") && !activeEvent.WindowBoundingRectangle.IsEmpty && (
        <div style={rectToStyle(cleanRect(activeEvent.WindowBoundingRectangle), '#22ff00ff')} /> // green
      )}
      {/* Element bounding rectangle (cyan) */}
      {!!viewConfig.overlayElement && activeEvent.ElementBoundingRectangle && !activeEvent.ElementBoundingRectangle.IsEmpty && (
        <div style={rectToStyle(cleanRect(activeEvent.ElementBoundingRectangle), '#00bcd4')} /> // cyan
      )}
      {/* Mouse click indicator */}
      {!!viewConfig.overlayMouseClick && mousePos && (
        <div style={mouseToStyle(mousePos)} />
      )}
    </>
  );
}

RectangleOverlay.propTypes = {
  events: PropTypes.array.isRequired,
  currentTime: PropTypes.number.isRequired,
  videoRef: PropTypes.object.isRequired,
  displayDuration: PropTypes.number,
};

export default RectangleOverlay;
